// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useState } from 'react';
import styled from '@emotion/styled';

import { DateTime } from 'infras/datetime';
import { CalendarDateDetail } from 'presentations/api/helpers/event_type';
import { TimeZoneListItem } from 'presentations/api/schema/time_zone';
import { SharedGuestUserItem } from 'presentations/api/schema/user';
import { spacing } from 'presentations/views/styles';
import { gray04 } from 'presentations/views/styles/color';

import SelectTimeZone from '../shared/components/SelectTimeZone';
import CalendarUnavailableButton from '../shared/components/CalendarUnavailableButton';
import DateTimeList from './components/DateTimeList';
import TimeZoneListPopover from 'presentations/views/components/organisms/TimeZoneListPopover';
import {
  TimeListViewerCalendarEvent,
  UnavailableInfomation,
} from 'presentations/api/schema/calendar_event';
import { CalendarViewType } from 'domains/models/event_type/calendarViewType';

type Props = {
  isLoading: boolean;
  isViewMoreLoading: boolean;
  timeZones: TimeZoneListItem[];
  timeZone: string | null;
  duration: number;
  updateTimeZone?: (timeZone: string) => void;
  calendarDateList: CalendarDateDetail[];
  color: string;
  isRevealGuestUser: boolean;
  isShowViewerCalendar: boolean;
  isHostDiagnosticMode: boolean;
  onSelect: (
    time: string,
    date: DateTime | undefined,
    users: SharedGuestUserItem[],
  ) => void;
  onClickBlockTime: (
    time: string,
    unavailableInformations: UnavailableInfomation[],
  ) => void;
  onClickEventName: (
    element: HTMLElement,
    event: TimeListViewerCalendarEvent,
  ) => void;
  onClickViewMore: () => void;
};

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const timeZone = props.timeZone || 'Asia/Tokyo';

  const [timeZoneAnchor, setTimeZoneAnchor] = useState<HTMLElement | null>(
    null,
  );

  const onCloseTimeZone = () => {
    setTimeZoneAnchor(null);
  };

  const onClickTimeZone = (e: SyntheticEvent<HTMLDivElement>) => {
    setTimeZoneAnchor(e.currentTarget);
  };

  const onSelectTimeZone = (name: string) => {
    if (!props.updateTimeZone) return;
    props.updateTimeZone(name);
    onCloseTimeZone();
  };

  return (
    <StyledContainer>
      <StyledRow>
        <SelectTimeZone
          isLoading={props.isLoading}
          timeZone={timeZone}
          timeZones={props.timeZones}
          onClick={onClickTimeZone}
          color={props.color}
        />
      </StyledRow>
      <StyledListContainer>
        {!props.isLoading && props.calendarDateList.length === 0 && (
          <StyledEmptyContainer>
            <CalendarUnavailableButton
              calendarViewType={CalendarViewType.ListTime}
              onClick={props.onClickViewMore}
              color={props.color}
            />
          </StyledEmptyContainer>
        )}
        <DateTimeList
          isLoading={props.isLoading}
          isViewMoreLoading={props.isViewMoreLoading}
          timeZone={timeZone}
          duration={props.duration}
          calendarDateList={props.calendarDateList}
          color={props.color}
          isRevealGuestUser={props.isRevealGuestUser}
          isShowViewerCalendar={props.isShowViewerCalendar}
          isHostDiagnosticMode={props.isHostDiagnosticMode}
          onSelect={props.onSelect}
          onClickBlockTime={props.onClickBlockTime}
          onClickEventName={props.onClickEventName}
          onClickViewMore={props.onClickViewMore}
        />
      </StyledListContainer>
      {timeZoneAnchor && (
        <TimeZoneListPopover
          title={t('Edit time zone')}
          anchor={timeZoneAnchor}
          onClose={onCloseTimeZone}
          onSelect={onSelectTimeZone}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        />
      )}
    </StyledContainer>
  );
}

const StyledContainer = styled.div``;
const StyledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const StyledListContainer = styled.div`
  margin-top: ${spacing(16)};
  padding-top: ${spacing(16)};
  border-top: 1px solid ${gray04};
`;
const StyledEmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 ${spacing(16)};
`;
